import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { GameEvent } from '@src/core/EventQueue';
import { kill } from '@src/ecs/archetypes';
import {
  BossTag,
  Damage,
  DragonTag,
  Dying,
  Enemy,
  EnemyKind,
  Health,
  Knockback,
  Pierce,
  Player,
  Position,
  Projectile,
  RECENT_HITS,
  Radius,
  Spawner,
  Sprite,
  SpriteId,
  Team,
  TeamTag,
  Velocity,
} from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { RoomPhase, requestDecal, type GameContext } from '@src/ecs/world';
import { dealDamage } from '@src/game/Combat';
import { BOUNDS, isOutside } from '@src/game/arena/RoomLayout';
import { bossDef } from '@src/game/BossDefs';

const CANDIDATES = 256;
const FLAME_SCORCH = 0x3a1608;
/** Gather pad past own body radius: must cover the widest other body (colossus 1.3, golem 0.9) plus slop. */
const ENEMY_GATHER_PAD = 1.4;
const COINCIDENT_EPS2 = 1e-8;

export function isEnemyFlyer(kind: number): boolean {
  return kind === EnemyKind.Skull || kind === EnemyKind.Bat || kind === EnemyKind.Cinder || kind === EnemyKind.Wraith;
}

/** Planted on the wall: never pushed by other bodies, never clamped to the floor. */
function isAnchored(kind: number): boolean {
  return kind === EnemyKind.Gargoyle || kind === EnemyKind.Spitter;
}

/** A mimic that has not woken yet (or is mid-wake) is furniture, not a body that bites. */
function mimicAsleep(eid: number): boolean {
  return Enemy.kind[eid] === EnemyKind.Mimic && Enemy.mode[eid] < 2;
}

/**
 * Radius used to keep enemies off each other. Sized to the sprite, not the hit
 * circle (Radius.r), which is what projectiles and the player collide against.
 */
export function bodyRadius(eid: number): number {
  const b = Config.collision.body;
  switch (Enemy.kind[eid]) {
    case EnemyKind.Imp:
    case EnemyKind.Hexer:
      return b.imp;
    case EnemyKind.Golem:
      return b.golem;
    case EnemyKind.Brute:
      return b.brute;
    case EnemyKind.Skull:
    case EnemyKind.Cinder:
      return b.skull;
    case EnemyKind.Wraith:
      return b.wraith;
    case EnemyKind.Slime:
      return b.slime;
    case EnemyKind.Blight:
      return b.blight;
    case EnemyKind.Mimic:
      return b.mimic;
    case EnemyKind.Bat:
      return b.bat;
    default:
      return Radius.r[eid];
  }
}

function collideMass(kind: number): number {
  const m = Config.collision.mass;
  switch (kind) {
    case EnemyKind.Golem:
      return m.golem;
    case EnemyKind.Brute:
      return m.brute;
    case EnemyKind.Slime:
      return m.slime;
    case EnemyKind.Blight:
      return m.blight;
    case EnemyKind.Mimic:
      return m.mimic;
    case EnemyKind.Skull:
    case EnemyKind.Cinder:
      return m.skull;
    case EnemyKind.Wraith:
      return m.wraith;
    case EnemyKind.Bat:
      return m.bat;
    case EnemyKind.Gargoyle:
    case EnemyKind.Spitter:
      return m.gargoyle;
    case EnemyKind.Boss:
      return m.boss;
    default:
      return m.imp;
  }
}

/** Combined collide radius. Flyers use nearly full size vs everyone, especially their own kind. */
export function enemyPairMinDist(aKind: number, aR: number, bKind: number, bR: number): number {
  const c = Config.collision;
  const aFly = isEnemyFlyer(aKind);
  const bFly = isEnemyFlyer(bKind);
  const sa = aFly ? (bFly ? c.flyerSame : c.flyerOver) : 1;
  const sb = bFly ? (aFly ? c.flyerSame : c.flyerOver) : 1;
  return aR * sa + bR * sb;
}

export interface OverlapStats {
  /** Living enemies counted. */
  bodies: number;
  /** Pairs closer than 0.8 x their collide distance (body circles, flyer rules applied). */
  pairs: number;
  /** Same, grounded-vs-grounded only. */
  groundedPairs: number;
  /** Same, flyer-vs-flyer only. */
  flyerPairs: number;
  /** Deepest overlap as 1 - dist / minDist (1 = coincident). */
  worst: number;
}

/**
 * DEV diagnostic: brute-force pair scan over living enemies using the separation
 * body radii. `scale` shrinks or inflates every radius (0.7 is roughly hit-radius).
 */
export function enemyOverlapStats(ctx: GameContext, scale = 1): OverlapStats {
  const enemies = query(ctx.world, [Enemy, Position, Radius, Health]);
  const out: OverlapStats = { bodies: 0, pairs: 0, groundedPairs: 0, flyerPairs: 0, worst: 0 };
  const alive: number[] = [];
  for (let i = 0; i < enemies.length; i++) {
    const e = enemies[i];
    if (Dying.flag[e] || Health.hp[e] <= 0 || isAnchored(Enemy.kind[e])) continue;
    alive.push(e);
  }
  out.bodies = alive.length;
  for (let i = 0; i < alive.length; i++) {
    const a = alive[i];
    const aFly = isEnemyFlyer(Enemy.kind[a]);
    for (let k = i + 1; k < alive.length; k++) {
      const b = alive[k];
      const dx = Position.x[b] - Position.x[a];
      const dy = Position.y[b] - Position.y[a];
      const minDist = enemyPairMinDist(Enemy.kind[a], bodyRadius(a) * scale, Enemy.kind[b], bodyRadius(b) * scale);
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist >= minDist * 0.8) continue;
      const bFly = isEnemyFlyer(Enemy.kind[b]);
      out.pairs++;
      if (!aFly && !bFly) out.groundedPairs++;
      else if (aFly && bFly) out.flyerPairs++;
      const depth = 1 - dist / minDist;
      if (depth > out.worst) out.worst = depth;
    }
  }
  return out;
}

/**
 * Circle-vs-circle collision using the enemy spatial grid.
 * Player projectiles hit enemies and spawners; enemy projectiles (dragon fire),
 * enemies and the dragon itself hit players. Living enemies then softly resolve
 * overlaps so bodies cannot occupy the same spot.
 */
export class CollisionSystem implements GameSystem {
  private ctx!: GameContext;
  private readonly candidates = new Int32Array(CANDIDATES);

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(_dt: number): void {
    this.projectilesVsTargets();
    this.enemiesVsPlayers();
    this.enemiesVsEnemies();
  }

  private alreadyHit(proj: number, target: number): boolean {
    const base = proj * RECENT_HITS;
    const n = Projectile.recentCount[proj];
    const count = n < RECENT_HITS ? n : RECENT_HITS;
    for (let i = 0; i < count; i++) {
      if (Projectile.recent[base + i] === target) return true;
    }
    return false;
  }

  private rememberHit(proj: number, target: number): void {
    const base = proj * RECENT_HITS;
    const n = Projectile.recentCount[proj];
    Projectile.recent[base + (n % RECENT_HITS)] = target;
    Projectile.recentCount[proj] = n + 1;
  }

  private projectilesVsTargets(): void {
    const ctx = this.ctx;
    const projectiles = query(ctx.world, [Projectile, Position, Radius, Damage, Pierce, Knockback, TeamTag]);
    const spawners = query(ctx.world, [Spawner, Position, Radius, Health]);
    const players = query(ctx.world, [Player, Position, Radius, Health]);
    const bosses = query(ctx.world, [BossTag, Position, Radius, Health]);

    for (let i = 0; i < projectiles.length; i++) {
      const p = projectiles[i];
      if (Dying.flag[p]) continue;
      const px = Position.x[p];
      const py = Position.y[p];
      const pr = Radius.r[p];

      if (isOutside(px, py, 3)) {
        kill(p);
        continue;
      }

      const vx = Velocity.x[p];
      const vy = Velocity.y[p];
      const speed = Math.hypot(vx, vy);
      const kb = Knockback.force[p];
      const kbX = speed > 0.01 ? (vx / speed) * kb : 0;
      const kbY = speed > 0.01 ? (vy / speed) * kb : 0;

      if (TeamTag.team[p] === Team.Enemy) {
        // Dragon fire: hits players, scorches the floor when it lands.
        for (let k = 0; k < players.length; k++) {
          const pid = players[k];
          if (Player.dead[pid] || Health.invuln[pid] > 0) continue;
          const dx = Position.x[pid] - px;
          const dy = Position.y[pid] - py;
          const rr = pr + Radius.r[pid];
          if (dx * dx + dy * dy > rr * rr) continue;
          if (this.alreadyHit(p, pid)) continue;
          let kx = kbX * 0.5;
          let ky = kbY * 0.5;
          if (speed < 0.01) {
            // Stationary growing rings (boss stomps, screeches) shove radially away from the source.
            const d = Math.hypot(dx, dy) || 1;
            kx = (dx / d) * kb;
            ky = (dy / d) * kb;
          }
          dealDamage(ctx, pid, Damage.amount[p], kx, ky, 1, Projectile.weapon[p]);
          this.rememberHit(p, pid);
          if (Sprite.id[p] === SpriteId.BoltFire) ctx.events.push(GameEvent.DragonBurn, pid, 0, px, py);
          if (Pierce.left[p]-- <= 0) {
            kill(p);
            break;
          }
        }
        if (Sprite.id[p] === SpriteId.BoltFire && !Dying.flag[p] && ctx.rng.chance(0.02)) {
          requestDecal(ctx.fx, px, py, FLAME_SCORCH, 0.6 + ctx.rng.next() * 0.5);
        }
        continue;
      }

      // Spawners (few; direct loop).
      for (let s = 0; s < spawners.length; s++) {
        const sid = spawners[s];
        if (Dying.flag[sid]) continue;
        const dx = Position.x[sid] - px;
        const dy = Position.y[sid] - py;
        const rr = pr + Radius.r[sid];
        if (dx * dx + dy * dy > rr * rr) continue;
        if (this.alreadyHit(p, sid)) continue;
        const dirX = speed > 0.01 ? vx / speed : dx / (Math.hypot(dx, dy) || 1);
        const dirY = speed > 0.01 ? vy / speed : dy / (Math.hypot(dx, dy) || 1);
        if (!dealDamage(ctx, sid, Damage.amount[p], dirX, dirY, Damage.spawnerMult[p], Projectile.weapon[p])) continue;
        this.rememberHit(p, sid);
        if (Pierce.left[p]-- <= 0) {
          kill(p);
          break;
        }
      }
      if (Dying.flag[p]) continue;

      // The boss body is wider than a grid cell, so it gets a direct check.
      for (let b = 0; b < bosses.length; b++) {
        const bid = bosses[b];
        if (Dying.flag[bid] || Health.hp[bid] <= 0) continue;
        const dx = Position.x[bid] - px;
        const dy = Position.y[bid] - py;
        const rr = pr + Radius.r[bid];
        if (dx * dx + dy * dy > rr * rr) continue;
        if (this.alreadyHit(p, bid)) continue;
        if (!dealDamage(ctx, bid, Damage.amount[p], kbX, kbY, 1, Projectile.weapon[p])) continue;
        this.rememberHit(p, bid);
        if (Pierce.left[p]-- <= 0) {
          kill(p);
          break;
        }
      }
      if (Dying.flag[p]) continue;

      const n = ctx.grid.gather(px, py, pr + 0.7, this.candidates);
      for (let k = 0; k < n; k++) {
        const e = this.candidates[k];
        if (Dying.flag[e] || Health.hp[e] <= 0 || Enemy.kind[e] === EnemyKind.Boss) continue;
        const dx = Position.x[e] - px;
        const dy = Position.y[e] - py;
        const rr = pr + Radius.r[e];
        if (dx * dx + dy * dy > rr * rr) continue;
        if (this.alreadyHit(p, e)) continue;
        // Radial weapons (shockwaves / arcs) push outward from the projectile center.
        let ox = kbX;
        let oy = kbY;
        if (speed < 0.01 || Projectile.grow[p] > 0) {
          const d = Math.hypot(dx, dy) || 1;
          ox = (dx / d) * kb;
          oy = (dy / d) * kb;
        }
        if (!dealDamage(ctx, e, Damage.amount[p], ox, oy, 1, Projectile.weapon[p])) continue;
        this.rememberHit(p, e);
        if (Pierce.left[p]-- <= 0) {
          kill(p);
          break;
        }
      }
    }
  }

  private enemiesVsPlayers(): void {
    const ctx = this.ctx;
    const players = query(ctx.world, [Player, Position, Radius, Health]);
    const dragons = query(ctx.world, [DragonTag, Position, Radius]);
    const bosses = query(ctx.world, [BossTag, Position, Radius, Health]);
    const contact = Config.player.contactDamage;

    for (let i = 0; i < players.length; i++) {
      const pid = players[i];
      if (Player.dead[pid] || Health.invuln[pid] > 0) continue;
      const px = Position.x[pid];
      const py = Position.y[pid];
      const pr = Radius.r[pid];

      for (let l = 0; l < dragons.length; l++) {
        const did = dragons[l];
        if (Dying.flag[did]) continue;
        const dx = Position.x[did] - px;
        const dy = Position.y[did] - py;
        const rr = pr + Radius.r[did];
        if (dx * dx + dy * dy > rr * rr) continue;
        const d = Math.hypot(dx, dy) || 1;
        dealDamage(ctx, pid, Config.dragon.damage, (-dx / d) * 7, (-dy / d) * 7, 1, 0);
      }
      if (Health.invuln[pid] > 0) continue;

      for (let b = 0; b < bosses.length; b++) {
        const bid = bosses[b];
        // Still arriving (falling in / rising / condensing): no body to bump into yet.
        if (Dying.flag[bid] || Health.hp[bid] <= 0 || BossTag.phase[bid] === 0) continue;
        const dx = Position.x[bid] - px;
        const dy = Position.y[bid] - py;
        const rr = pr + Radius.r[bid];
        if (dx * dx + dy * dy > rr * rr) continue;
        const d = Math.hypot(dx, dy) || 1;
        const archmage = bossDef(BossTag.kind[bid]).archmage;
        // A charging champion hits like a wall.
        const charging = BossTag.lockX[bid] !== 0 || BossTag.lockY[bid] !== 0;
        const dmg = (archmage ? Config.boss.contactDamage : Config.boss.champion.contactDamage) * (charging ? 1.5 : 1);
        const push = charging ? 16 : 11;
        dealDamage(ctx, pid, dmg, (-dx / d) * push, (-dy / d) * push, 1, 0);
      }
      if (Health.invuln[pid] > 0) continue;

      const n = ctx.grid.gather(px, py, pr + 0.8, this.candidates);
      for (let k = 0; k < n; k++) {
        const e = this.candidates[k];
        if (Dying.flag[e] || Health.hp[e] <= 0 || Enemy.kind[e] === EnemyKind.Boss) continue;
        const dx = Position.x[e] - px;
        const dy = Position.y[e] - py;
        const rr = pr + Radius.r[e];
        if (dx * dx + dy * dy > rr * rr) continue;
        const kind = Enemy.kind[e];
        // Sleeping chests and faded wraiths have no body to bump.
        if (mimicAsleep(e) || (kind === EnemyKind.Wraith && Health.invuln[e] > 0)) continue;
        const d = Math.hypot(dx, dy) || 1;
        if (kind === EnemyKind.Cinder) {
          // Touching a cinder sets it off. The blast (DamageSystem) is what hurts.
          Health.hp[e] = 0;
          continue;
        }
        const heavy = kind === EnemyKind.Golem || kind === EnemyKind.Brute;
        const boss = kind === EnemyKind.Boss;
        // A charging brute (mode 2, see EnemyAISystem) hits like a wall, then the charge is spent.
        const charging = kind === EnemyKind.Brute && Enemy.mode[e] === 2;
        const dmg = (boss ? Config.boss.contactDamage : heavy ? contact * 2.5 : contact) * (charging ? 1.4 : 1);
        const push = boss ? 11 : charging ? 15 : heavy ? 9 : 4;
        dealDamage(ctx, pid, dmg, (-dx / d) * push, (-dy / d) * push, 1, 0);
        if (charging) {
          Enemy.mode[e] = 0;
          Enemy.beat[e] = Config.enemies.brute.chargeRest;
          Velocity.x[e] *= 0.2;
          Velocity.y[e] *= 0.2;
        }
        // Bounce the attacker off so the swarm doesn't chain-hit instantly.
        if (!isAnchored(kind) && !boss && !charging) {
          Velocity.x[e] += (dx / d) * 5;
          Velocity.y[e] += (dy / d) * 5;
        }
        break;
      }
    }
  }

  /**
   * Soft circle resolve for living enemies. Several Gauss-Seidel passes so a
   * coincident pile can open in one tick; each pair is mass-split along the
   * contact normal and closing speed is cancelled. Dying bodies are skipped.
   */
  private enemiesVsEnemies(): void {
    const enemies = query(this.ctx.world, [Enemy, Position, Velocity, Radius, Health]);
    const passes = Config.collision.iterations;
    for (let pass = 0; pass < passes; pass++) {
      if (pass > 0) this.refillEnemyGrid(enemies);
      this.resolveEnemyPass(enemies);
    }
  }

  private refillEnemyGrid(enemies: ArrayLike<number>): void {
    const grid = this.ctx.grid;
    grid.clear();
    for (let i = 0; i < enemies.length; i++) {
      const eid = enemies[i];
      if (Dying.flag[eid] || Health.hp[eid] <= 0) continue;
      grid.insert(eid, Position.x[eid], Position.y[eid]);
    }
  }

  private resolveEnemyPass(enemies: ArrayLike<number>): void {
    const ctx = this.ctx;
    const c = Config.collision;

    for (let i = 0; i < enemies.length; i++) {
      const a = enemies[i];
      if (Dying.flag[a] || Health.hp[a] <= 0) continue;
      const aR = bodyRadius(a);
      const n = ctx.grid.gather(Position.x[a], Position.y[a], aR + ENEMY_GATHER_PAD, this.candidates);
      for (let k = 0; k < n; k++) {
        const b = this.candidates[k];
        if (b <= a || Dying.flag[b] || Health.hp[b] <= 0) continue;

        const dx = Position.x[b] - Position.x[a];
        const dy = Position.y[b] - Position.y[a];
        const d2 = dx * dx + dy * dy;
        const minDist = enemyPairMinDist(Enemy.kind[a], aR, Enemy.kind[b], bodyRadius(b)) + c.slop;
        if (d2 > minDist * minDist) continue;

        let dist: number;
        let nx: number;
        let ny: number;
        if (d2 < COINCIDENT_EPS2) {
          const angle = a * 2.3999632 + b;
          nx = Math.cos(angle);
          ny = Math.sin(angle);
          dist = 0;
        } else {
          dist = Math.sqrt(d2);
          nx = dx / dist;
          ny = dy / dist;
        }

        // Sleeping mimics hold their spot like furniture.
        const aAnchored = isAnchored(Enemy.kind[a]) || mimicAsleep(a);
        const bAnchored = isAnchored(Enemy.kind[b]) || mimicAsleep(b);
        const invA = aAnchored ? 0 : 1 / collideMass(Enemy.kind[a]);
        const invB = bAnchored ? 0 : 1 / collideMass(Enemy.kind[b]);
        const invSum = invA + invB;
        if (invSum <= 0) continue;
        const push = Math.min((minDist - dist) * c.relax, c.maxPush);
        this.clampBody(a, Position.x[a] - nx * push * (invA / invSum), Position.y[a] - ny * push * (invA / invSum));
        this.clampBody(b, Position.x[b] + nx * push * (invB / invSum), Position.y[b] + ny * push * (invB / invSum));

        const closing = (Velocity.x[b] - Velocity.x[a]) * nx + (Velocity.y[b] - Velocity.y[a]) * ny;
        if (closing >= 0) continue;
        const j = (closing * c.damp) / invSum;
        Velocity.x[a] += nx * j * invA;
        Velocity.y[a] += ny * j * invA;
        Velocity.x[b] -= nx * j * invB;
        Velocity.y[b] -= ny * j * invB;
      }
    }
  }

  private clampBody(eid: number, x: number, y: number): void {
    if (isAnchored(Enemy.kind[eid])) {
      Position.x[eid] = x;
      Position.y[eid] = y;
      return;
    }
    if (this.ctx.room.phase !== RoomPhase.Transition) {
      const r = Radius.r[eid];
      if (x < BOUNDS.minX + r) x = BOUNDS.minX + r;
      else if (x > BOUNDS.maxX - r) x = BOUNDS.maxX - r;
      if (y < BOUNDS.minY + r) y = BOUNDS.minY + r;
      else if (y > BOUNDS.maxY - r) y = BOUNDS.maxY - r;
    }
    Position.x[eid] = x;
    Position.y[eid] = y;
  }
}